import { ContentLoading } from "../components/ContentLoading";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { FieldError } from "../components/ui/field";
import { listProvisioningRuns, retryProvisioningRun } from "./api";

function currentRuns<T extends { organizationId: string }>(runs: readonly T[] | undefined): T[] {
  const current: T[] = [];
  const organizations = new Set<string>();
  for (const run of runs ?? []) {
    if (organizations.has(run.organizationId)) continue;
    organizations.add(run.organizationId);
    current.push(run);
  }
  return current;
}

export function ProvisioningView() {
  const queryClient = useQueryClient();
  const runs = useQuery({ queryKey: ["ops", "provisioning"], queryFn: listProvisioningRuns });
  const retry = useMutation({
    mutationFn: retryProvisioningRun,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["ops", "provisioning"] }),
        queryClient.invalidateQueries({ queryKey: ["ops", "customers"] }),
      ]);
    },
  });
  const visibleRuns = currentRuns(runs.data?.runs);
  return (
    <section>
      <h1 className="text-2xl font-bold">Provisioning</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        A customer is ready only after every isolated resource passes verification.
      </p>
      {runs.error ? (
        <div className="mt-4">
          <FieldError>{runs.error.message}</FieldError>
        </div>
      ) : null}
      {retry.error ? (
        <div className="mt-4">
          <FieldError>{retry.error.message}</FieldError>
        </div>
      ) : null}
      <div className="mt-5 space-y-4">
        {runs.isPending ? <ContentLoading label="Loading provisioning…" variant="cards" /> : null}
        {visibleRuns.map((run) => (
          <Card key={run.id}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between gap-3">
                <span>{run.customerName}</span>
                <span className="font-mono text-xs">
                  {run.status === "ready" ? "Provisioned" : run.status}
                </span>
              </CardTitle>
              <CardDescription>
                Attempt {run.attempt} · {run.id}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-2 sm:grid-cols-5">
                {run.steps.map((step) => (
                  <div className="rounded-sm border p-2" key={step.key}>
                    <p className="font-mono text-xs">{step.key}</p>
                    <p className="mt-1 text-xs capitalize text-muted-foreground">{step.status}</p>
                  </div>
                ))}
              </div>
              {run.lastErrorCode ? (
                <p className="font-mono text-xs text-destructive">{run.lastErrorCode}</p>
              ) : null}
              {run.status === "failed" || run.status === "degraded" ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    retry.mutate(run.id);
                  }}
                  disabled={retry.isPending}
                >
                  <RefreshCw /> Retry safely
                </Button>
              ) : null}
            </CardContent>
          </Card>
        ))}
        {!runs.isPending && !runs.error && visibleRuns.length === 0 ? (
          <p className="rounded-sm border border-dashed p-5 text-sm text-muted-foreground">
            No provisioning runs yet.
          </p>
        ) : null}
      </div>
    </section>
  );
}
